<template lang="pug">
  .address-col
    .rows
      .row.header(:class="{'disabled': disabled}")
        p.cell {{title}}
        p.cdflabel(v-if="showCDF") {{cdfcode}}
        a.toggler(v-if="needMap" @click="toggleMap")
          font-awesome-icon.icon(:class="{'close': !showMap}" icon="map-marker-alt")
          | {{togglerText}}
      address-row(
        v-if="showRow(value)"
        v-for="({ tooltip, value, jsonPath}, i) in parsedAddress"
        :key="jsonPath + i"
        :jsonPath="jsonPath"
        :address="value"
        :tooltip="tooltip"
      )
    address-map(v-if="needMap" :address="address" :response="response" v-show="showMap")
    el-dropdown(
      v-if="needMap"
      v-show="showMap"
      trigger="click"
      size="medium"
      placement="right-start"
      @command="handleMap"
      )
      lei-btn.config-btn(
        type="turquoise-keyline"
        size="sm"
        fill) Show in...
      el-dropdown-menu(slot="dropdown" :offset="-5")
        el-dropdown-item(command="bing") Bing maps
        el-dropdown-item(command="google") Google maps
        el-dropdown-item(command="openStreet") Open Street maps
</template>
<script>
import { mapState, mapMutations, mapGetters, } from 'vuex'
import { SET_ADDRESS_DATA, } from '@/store/modules/record/mutations-types'
import Challenge from '@/mixins/challenge'
import AddressMap from './AddressMap'
import AddressRow from './AddressRow'
import { getCountryNameByCode, } from '@/utils/countries'
import { getCDF, } from '../../../utils/cdf'
import LeiPopover from '@/components/lei-components/LeiPopover'
import LeiBtn from '@/components/lei-components/LeiBtn'

function getAddressResponse () {
  const { addressLines = [], postalCode, city, } = this.address
  const addressLine = addressLines[0] ? addressLines[0].value : undefined
  this.loading = true
  fetch(
    `https://nominatim.openstreetmap.org/search.php?street=${addressLine}&city=${city.value}&postalcode=${postalCode.value}&format=jsonv2`
  )
    .then((res) => res.json())
    .then((res) => {
      if (res.length > 0) {
        this.loading = false
        this.setAddress({
          res,
          type: this.title,
        })
        this.response = res
        this.mapLinks = {
          google: `http://maps.google.com?z=12&t=m&q=loc:${res[0].lat}+${res[0].lon}`,
          bing: `https://bing.com/maps?cp=${res[0].lat}~${res[0].lon}&lvl=19&style=r&sp=point.${res[0].lat}_${res[0].lon}_${addressLine}`,
          openStreet: `http://www.openstreetmap.org/?mlat=${res[0].lat}&mlon=${res[0].lon}&zoom=19&layers=M`,
        }
      }
    })
    .catch((e) => console.error(e))
}

export default {
  components: {
    LeiBtn,
    AddressMap,
    LeiPopover,
    AddressRow,
  },
  mixins: [ Challenge, ],
  props: {
    address: {
      type: Object,
      required: true,
    },
    root: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
  },
  data () {
    return {
      showMap: true,
      loading: true,
      response: {},
      mapLinks: {
        google: 'http://maps.google.com',
        bing: 'https://bing.com/maps/default.aspx',
        openStreet: 'https://www.openstreetmap.org',
      },
    }
  },
  computed: {
    disabled () {
      return this.challengeMode && this.addressesSectionIsBlocked
    },
    parsedAddress () {
      return this.parseAddressBlock(this.address, true)
    },
    needMap () {
      return (this.title === 'Legal' || this.title === 'Headquarters') && !this.loading && !this.challengeMode
    },
    togglerText () {
      return this.showMap ? 'Hide map' : 'Show on map'
    },
    cdfcode () {
      return getCDF(this.title)
    },

    ...mapState('record', [ 'showEmptyFields', 'showCDF', 'addressData', ]),
    ...mapGetters('record', [ 'addressesSectionIsBlocked', ]),
    ...mapGetters('record/challenge', [ 'getAllSavedFieldsByPath', 'getSavedFieldByPath', ]),

  },
  watch: {
    '$route' (to) {
      if (to.params.currentTab === 'record') {
        const { addressData, type, } = this.addressData
        if (!addressData) getAddressResponse.call(this)
        if (addressData && type === this.title) {
          this.loading = false
          const { addressLines, } = this.address
          const addressLine = addressLines[0] ? addressLines[0].value : undefined
          this.response = addressData
          const res = addressData
          this.mapLinks = {
            google: `http://maps.google.com?z=12&t=m&q=loc:${res[0].lat}+${res[0].lon}`,
            bing: `https://bing.com/maps?cp=${res[0].lat}~${res[0].lon}&lvl=19&style=r&sp=point.${res[0].lat}_${res[0].lon}_${addressLine}`,
            openStreet: `http://www.openstreetmap.org/?mlat=${res[0].lat}&mlon=${res[0].lon}&zoom=19&layers=M`,
          }
        }
      }
    },
  },
  created () {
    const { addressData, type, } = this.addressData
    if (!addressData && this.$route.params.currentTab !== 'record_modifications') getAddressResponse.call(this)
    if (addressData && this.$route.params.currentTab !== 'record_modifications' && type === this.title){
      this.loading = false
      const { addressLines, } = this.address
      const addressLine = addressLines[0] ? addressLines[0].value : undefined
      this.response = addressData
      const res = addressData
      this.mapLinks = {
        google: `http://maps.google.com?z=12&t=m&q=loc:${res[0].lat}+${res[0].lon}`,
        bing: `https://bing.com/maps?cp=${res[0].lat}~${res[0].lon}&lvl=19&style=r&sp=point.${res[0].lat}_${res[0].lon}_${addressLine}`,
        openStreet: `http://www.openstreetmap.org/?mlat=${res[0].lat}&mlon=${res[0].lon}&zoom=19&layers=M`,
      }
    }
  },
  methods: {
    isEmpty: (value) => !value || value === '',
    countryRow: (country) => {
      const name = country.value ? getCountryNameByCode(country.value) : null
      return {
        ...country,
        value: {
          name,
          code: country.value,
        },
      }
    },
    parseAddressBlock (
      {
        country,
        addressLines = [],
        postalCode, city,
        region,
        addressNumber,
        addressNumberWithinBuilding,
        mailRouting,
      },
      needParse = false
    ) {
      return [
        ...this.parseAddressLines('Address lines', addressLines),
        this.parseRowWithTooltip('Address number', addressNumber),
        this.parseRowWithTooltip('Address number within building', addressNumberWithinBuilding),
        this.parseRowWithTooltip('Mail routing', mailRouting),
        this.parseRowWithTooltip('Postal code', postalCode),
        this.parseRowWithTooltip('City', city),
        this.parseRowWithTooltip('Region', region),
        needParse
          ? this.parseRowWithTooltip('Country', this.countryRow(country))
          : this.parseRowWithTooltip('Country', country),
      ]
    },
    parseAddressLines (tooltip, value) {
      if (Array.isArray(value)) {
        return value.map((item, idx) => {
          if (idx === 0) return this.parseRowWithTooltip('First Address Line', item, `addressLines[${idx}]`)
          return this.parseRowWithTooltip(tooltip, item, `addressLines[${idx}]`)
        })
      }
    },
    parseRowWithTooltip (tooltip, data) {
      return {
        ...data,
        tooltip,
      }
    },
    toggleMap () {
      this.showMap = !this.showMap
    },
    showRow (value) {
      return this.showEmptyFields || !this.isEmpty(value)
    },
    handleMap (map){
      window.open(this.mapLinks[map], '_blank')
    },
    ...mapMutations('record', {
      setAddress: SET_ADDRESS_DATA,
    }),
  },
}
</script>
<style lang="stylus" scoped>
  @import "../../../assets/styles/variables.styl"

  .el-dropdown
    align-self flex-start

  .address-col
    min-width calc(50% - 5px)
    flex-direction column
    flex 1
    display flex
    margin-right 5px
    margin-left 5px
    justify-content space-between

    &:nth-child(odd)
      margin-left 0
      z-index 1

      &:last-child
        margin-right 0

    &:nth-child(even)
      margin-right 0
      z-index 0

    .toggler
      display flex
      align-items center
      font-weight 600
      text-decoration none
      padding-bottom 3px
      border-bottom 1px solid $turquoise-color

      &:hover
        color $turquoise-color

      .icon
        font-size 120%
        margin-right 0.4rem

        &.close
          color $danger-color


    .row
      margin-bottom 2px
      min-height 45px
      width 100%
      display flex
      align-items center
      justify-content space-between
      padding 15px 20px
      background-color $default-color-light-2
      flex-direction row

      .cdflabel
        font-size 13px
        margin-top 6px

      .cell
        color $default-color-bold
        font-weight 600

      &.disabled
        background-color $disabled-bg
        .cell
          color $disabled-color

    .config-btn
      width fit-content

  @media (max-width: $tablet)
    .address-col
      margin 0
      border-bottom 1px solid $default-color-light-1

      .row
        margin-bottom 2px
        min-height auto
        background-color transparent
        .cell
          color $default-color-middle
          font-weight 600

</style>



// WEBPACK FOOTER //
// src/components/record-details/address/AddressColInline.vue